import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { displayTraceId } from '@core/interceptors/trace-context';
import { SVGIcon } from '@data/svg-icons.data';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MESSAGE_BOX_COPIED_SNACKBAR_DURATION_MS, MESSAGE_BOX_STYLES, MessageBoxStyle } from './message-box.const';
import { MessageBoxType } from './message-box.types';

/**
 * An inline notice — a coloured, left-bordered box with an icon and a translated message.
 * Optionally shows the request's "Trace ID" underneath with a copy button, so support finds the request in the logs (C23).
 */
@Component({
  selector: 'app-message-box',
  imports: [TranslatePipe, MatIconModule, MatTooltip],
  templateUrl: './message-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessageBoxComponent {
  readonly #clipboard = inject(Clipboard);
  readonly #snackBar = inject(MatSnackBar);
  readonly #translateService = inject(TranslateService);

  /** Translation key (or already translated text) to show. */
  public readonly message = input.required<string>();
  /** Visual variant; decides the colours and the default icon. */
  public readonly type = input<MessageBoxType>('primary');
  /** Overrides the variant's icon. */
  public readonly icon = input<SVGIcon>();
  /** API activity id / trace id of the failed request; shown under the message as "Trace ID", with a copy button. */
  public readonly activityId = input<string>();

  /** The trace id shown to the agent (a W3C activity id is reduced to its 32-hex trace id). */
  public readonly traceId = computed<string | undefined>(() => displayTraceId(this.activityId()));

  readonly #style = computed<MessageBoxStyle>(() => MESSAGE_BOX_STYLES[this.type()]);

  /** Icon shown next to the message — the explicit one, else the variant's. */
  public readonly displayIcon = computed<SVGIcon>(() => this.icon() ?? this.#style().icon);

  /** Background and border classes of the box. */
  public readonly boxClass = computed<string>(() => `${this.#style().background} ${this.#style().border}`);

  /** Colour class of the icon — the border's shade. */
  public readonly iconClass = computed<string>(() => this.#style().iconText);

  /** Colour class of the text. */
  public readonly textClass = computed<string>(() => this.#style().text);

  /** Copies the trace id and confirms it with a snackbar. */
  public onCopyToClipboard(value: string): void {
    this.#clipboard.copy(value);
    this.#snackBar.open(this.#translateService.instant('Copied to clipboard'), '', {
      panelClass: 'snackbar-success',
      duration: MESSAGE_BOX_COPIED_SNACKBAR_DURATION_MS,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }
}
